import { useTranslation } from 'react-i18next';

import { Card } from '@codes/ui-kit/components/ui/card';
import { Image } from '@codes/ui-kit/components/ui/image';
import { CardView } from '@lemoncloud/lemon-payments-api';
import { DeleteCardDialog } from '@web/app/features/my-page/components/DeleteCardDialog';
import { Trash } from 'lucide-react';

import ErrorIcon from '/assets/icons/error.svg';

interface CardItemProps {
    card: CardView;
    onDefaultCardClick: (card: CardView) => void;
    onDeleteCard: (card: CardView) => void;
}

export const CardItem = ({ card, onDefaultCardClick, onDeleteCard }: CardItemProps) => {
    const { t } = useTranslation();

    const isExpired = () => {
        if (card.isValid) {
            return false;
        }
        const today = new Date();
        const currentYear = today.getFullYear();
        const currentMonth = today.getMonth() + 1;
        return card.expYear < currentYear || (card.expYear === currentYear && card.expMonth < currentMonth);
    };

    return (
        <Card className="rounded-lg border border-[#F0F0F0] dark:border-[#5F5F5F] dark:bg-[#252525] shadow-none p-6">
            <div className="flex items-center justify-between max-xs:flex-col max-xs:items-start max-xs:gap-2">
                <div className="flex items-center gap-[6px]">
                    <div className="font-medium">
                        {card.brandEn || card.brand}{' '}
                        <span className="text-sm text-sub-title font-normal">({card.last4})</span>
                    </div>
                    {isExpired() && (
                        <div className="py-[3px] px-2 bg-[#222325] dark:bg-[#ABABAB] font-medium text-white dark:text-[#02060E] rounded text-[10px] inline-flex items-center justify-center gap-1">
                            <Image className="w-[10px]" src={ErrorIcon} alt="" />
                            {t('myPage.unavailable')}
                        </div>
                    )}
                    {card.isDefault && (
                        <div className="py-[3px] px-2 bg-sub-point text-white rounded text-[10px] inline-flex items-center justify-center">
                            {t('myPage.default')}
                        </div>
                    )}
                </div>
                <div className="flex items-center gap-[6px]">
                    {isExpired() && (
                        <DeleteCardDialog
                            trigger={
                                <button className="bg-time-box text-[#D24657] text-sm py-1 px-2 flex items-center gap-1 rounded border border-transparent hover:border-[#D24657] dark:text-white dark:hover:bg-[#993C4A]">
                                    <Trash className="w-[14px] h-[14px]" />
                                    {t('myPage.deleteCard')}
                                </button>
                            }
                            onDelete={() => onDeleteCard(card)}
                        />
                    )}
                    {!card.isDefault && !isExpired() && (
                        <>
                            <button
                                className="bg-hover text-sub text-sm py-1 px-2 rounded border border-transparent hover:border-sub-title"
                                onClick={() => onDefaultCardClick(card)}
                            >
                                {t('myPage.setDefaultCard')}
                            </button>
                            <span className="text-divider">|</span>
                            <DeleteCardDialog
                                trigger={
                                    <button className="bg-time-box text-[#D24657] text-sm py-1 px-2 flex items-center gap-1 rounded border border-transparent hover:border-[#D24657] dark:text-white dark:hover:bg-[#993C4A]">
                                        <Trash className="w-[14px] h-[14px]" />
                                        {t('myPage.deleteCard')}
                                    </button>
                                }
                                onDelete={() => onDeleteCard(card)}
                            />
                        </>
                    )}
                </div>
            </div>
            {isExpired() && <div className="text-xs text-sub-title mt-[7px]">{t('myPage.cardError')}</div>}
        </Card>
    );
};
